import { PopupRequest } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { DefaultButton, Dialog, DialogFooter, DialogType, IContextualMenuProps, MessageBar, MessageBarType, PrimaryButton, Spinner, Stack, Text } from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import { panelButtonsStyles } from 'global/Application';
import { Constants } from 'global/Application/Constants';
import { useApplication } from 'global/Application/Context';
import { useAuthentication } from 'global/Authentication';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from "react-router-dom";
import "./Login.css";
import { Delay } from 'global/Utility/TSFunctions';

//TODO: move this to useAuthentication context once the other login is clear
const Login = () => {
    const [processing, setProcessing] = useState(true);

    // lets start with silent msal and if we run into any issues, switch to manual.
    const [error, setError] = useState<any>(null);

    const { instance, accounts } = useMsal();

    const authentication = useAuthentication();
    const application = useApplication();

    const history = useHistory();
    const location = useSelector(state => state);

    const labelId: string = useId('dialogLabel');
    const subTextId: string = useId('subTextLabel');

    useEffect(() => {
        document.title = "Login";

        // Strict-mode/Debug causes this to run twice, so we need to set a timeout to only run once
        const _timerId = setTimeout(() => {
            // TODO check if we have an account, try login silently since msal cookies might still be valid
            tryLoginAsync()

        }, 100);

        return () => {
            clearTimeout(_timerId);
        };

    }, []);

    const dialogStyles = { main: { maxWidth: 450 } };

    const modalProps = useMemo(
        () => ({
            titleAriaId: labelId,
            subtitleAriaId: subTextId,
            isBlocking: false,
            styles: dialogStyles,
        }),
        [labelId, subTextId],
    );

    const dialogContentProps = {
        type: DialogType.largeHeader,
        title: 'Authentication',
        closeButtonAriaLabel: 'Close',
        subText: 'Please authenticate to continue.',
    };

    //TODO: this is really messy routing
    // @ts-ignore
    const from = { ...location.router.location, pathname: location.router.location.pathname.replace(/\/editor\/records\//i, "") } || { pathname: "/" };

    const tryLoginAsync = async () => {
        console.log("Login start");

        try {

            setProcessing(true);

            const loginRequest: PopupRequest = {
                scopes: ["User.Read"],
                redirectUri: Constants.portal.baseUrl
            };

            const request = {
                ...loginRequest,
                account: accounts[0],
                scopes: Constants.crm.scopes
            };

            await instance.acquireTokenSilent(request);

            console.log("Login identity");

            const identity = await application.dataSource.whoAmI_getAsync();

            console.log("Login complete");
            authentication.login(identity);
            history.replace(from);

        } catch (error: any) {
            console.error("Error acquiring token\n", error);

            const errorStr = error.toString()

            //BrowserAuthError: no_account_error: No account object provided to acquireTokenSilent and no active account has been set. Please call setActiveAccount or provide an account on the request.
            if (errorStr.indexOf('no_account_error:') > -1)
                setError("Account not found. Please authenticate.");

            // InteractionRequiredAuthError: interaction_required: AADSTS160021: Application requested a user session which does not exist. Trace ID: dbef541b-a164-49bb-b0c5-4cbd595f0700 Correlation ID: 40f96df3-2e18-4a28-9408-dedea12a6e12 Timestamp: 2025-04-18 07:04:57Z
            else if (errorStr.indexOf('interaction_required') > -1)
                setError("Session expired, Please authenticate.");
            else
                setError(error.toString());

        } finally {
            setProcessing(false);
        }

    }

    const menuProps: IContextualMenuProps = {
        items: [
            {
                key: 'loginRedirect',
                text: 'Login Redirect',
                iconProps: { iconName: 'SignIn' },
                onClick: () => {
                    setProcessing(true);

                    const loginRequest: PopupRequest = {
                        scopes: ["User.Read"],
                        redirectUri: Constants.portal.baseUrl
                    };

                    const request = {
                        ...loginRequest,
                        account: accounts[0],
                        scopes: Constants.crm.scopes
                    };

                    setProcessing(true);
                    // Microsoft appears to have a bug
                    // this immediately throws an exception and somehow causes the component to unmount and remount again
                    // so the silent login (automatic login attempt via useeffect remount) takes care of actually login
                    // instance.acquireTokenPopup(request);
                    instance.loginRedirect(request);
                }
            },
        ],
    };

    return <Dialog hidden={false} dialogContentProps={dialogContentProps} modalProps={modalProps} minWidth={600}>
        {processing && (
            <div>
                <Spinner label="Processing Login information" labelPosition="right" />
            </div>
        )}
        {!processing && (
            <Stack>
                <Text>
                    Authentication could not be confirmed.
                </Text>
                <Text>
                    Please click the Authenticate button to try again, or refresh the page.
                </Text>
                {error && (
                    <div style={{ margin: '1em' }}>

                        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}                >
                            {error}
                        </MessageBar>
                    </div>
                )}
                <DialogFooter>
                    <PrimaryButton text="Authenticate"
                        menuProps={menuProps}
                        split
                        onClick={async () => {

                            setProcessing(true);

                            const loginRequest: PopupRequest = {
                                scopes: ["User.Read"],
                                redirectUri: Constants.portal.baseUrl
                            };

                            const request = {
                                ...loginRequest,
                                account: accounts[0],
                                scopes: Constants.crm.scopes
                            };

                            // Microsoft appears to have a bug
                            // this immediately throws an exception and somehow causes the component to unmount and remount again
                            // so the silent login (automatic login attempt via useeffect remount) takes care of actually login
                            // await instance.acquireTokenPopup(request);
                            await instance.loginPopup(request);

                            // this is a workaround for the above issue, we need to wait for the component to remount or if it refreshes it will automatically try to login again
                            await Delay(500);

                            await tryLoginAsync();
                        }}
                    />
                    <DefaultButton text="Cancel" disabled styles={panelButtonsStyles} />
                </DialogFooter>
            </Stack>
        )}
    </Dialog>;
}

export default Login